import {skipToken} from '@reduxjs/toolkit/query';
import {Action, Card, openDeleteDialog, showNotification} from 'platform/components';
import {Grid, GridItem, Show, VStack} from 'platform/foundation';

import {any, isNotNil} from 'ramda';

import {useGetVehicleQuery} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {
  type CustomerInquiryResponseBody,
  useGetCustomerInquiryQuery,
  useGetWorkshopInterestQuery,
  useRemoveWorkshopInterestVehicleMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {testIds, vehiclesRoutes} from '@dms/routes';
import {
  CommentsWidget,
  EntityCustomFields,
  getVehicleSaleTypeFlagProps,
  handleApiError,
  NoteWidget,
  Section,
  useWorkshopInterestEditPermissions,
  VehicleWidgetCard,
} from '@dms/shared';

import {buildArray, composePath} from 'shared/web';

import {RequestSpecification} from '../../components/RequestSpecification';

type OverviewProps = {
  interestId: string;
};

function hasRequestSpecificationData(customerInquiry: CustomerInquiryResponseBody | undefined) {
  return any(isNotNil, [
    customerInquiry?.vehicleDetails.licensePlate,
    customerInquiry?.vehicleDetails.vin,
    customerInquiry?.vehicleDetails.make,
    customerInquiry?.vehicleDetails.modelFamily,
    customerInquiry?.vehicleDetails.trim,
    customerInquiry?.vehicleDetails.variant,
    customerInquiry?.vehicleDetails.firstRegistration,
    customerInquiry?.vehicleDetails.realProductionYear,
    customerInquiry?.vehicleDetails.realProductionMonth,
    customerInquiry?.vehicleInformation.currentMileage,
  ]);
}

export function Overview(props: OverviewProps) {
  const {
    hasSetNotePermission,
    hasEditCustomFieldsPermission,
    hasReadCustomFieldsPermission,
    hasReadCommentsPermission,
    hasAddCommentPermission,
    hasUpdateCommentPermission,
    hasDeleteCommentPermission,
    hasRemoveVehiclePermission,
  } = useWorkshopInterestEditPermissions({interestId: props.interestId});

  const {data: workshopInterest} = useGetWorkshopInterestQuery({id: props.interestId});

  const vehicleId =
    workshopInterest?.id === props.interestId ? workshopInterest?.vehicleId : undefined;
  const isVehicleSelected = isNotNil(vehicleId);

  const {data: customerInquiry} = useGetCustomerInquiryQuery(
    {interestId: props.interestId},
    {skip: isVehicleSelected, refetchOnMountOrArgChange: true}
  );

  const {data: vehicle} = useGetVehicleQuery(isNotNil(vehicleId) ? {vehicleId} : skipToken);

  const [removeVehicle, {isLoading: isRemoving}] = useRemoveWorkshopInterestVehicleMutation();

  const onRemoveVehicle = () => {
    removeVehicle({id: props.interestId})
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'));
      })
      .catch(handleApiError);
  };

  return (
    <Section>
      <Grid columns={2}>
        <Show when={vehicle}>
          {(vehicle) => (
            <GridItem span={2}>
              <Card title={i18n.t('entity.vehicle.labels.vehicle')}>
                <VehicleWidgetCard
                  data-testid={testIds.workshop.interestDetail('overview-vehicle')}
                  vehicles={[
                    {
                      id: vehicle.id,
                      vin: vehicle.vin,
                      registrationPlate: vehicle.vehicle?.registrationPlate,
                      firstRegistration: vehicle.vehicle?.firstRegistrationOn,
                      make: vehicle.make,
                      vehicleType: vehicle.type?.toString(),
                      modelFamily: vehicle.modelFamily,
                      trim: vehicle.trim,
                      title: vehicle.title,
                      variant: vehicle.variant,
                      mileage: vehicle.vehicle?.mileage,
                      transmission: vehicle.transmission,
                      fuelType: vehicle.fuelType,
                      bodyStyle: vehicle.bodyStyle,
                      driveType: vehicle.drive,
                      photos: vehicle.photos,
                      manufacturedOn: vehicle.vehicle?.manufacturedOn,
                      flags: [getVehicleSaleTypeFlagProps(vehicle.saleType)].filter(isNotNil),
                      actions: buildArray<Action>()
                        .add({
                          'data-testid': testIds.workshop.interestDetail('overview-vehicle-detail'),
                          type: 'button',
                          title: i18n.t('entity.vehicle.labels.detail'),
                          variant: 'outlined',
                          leftIcon: 'action/launch',
                          onClick: () => {
                            window.open(
                              composePath(vehiclesRoutes.detail, {params: {id: vehicle.id}}),
                              '_blank'
                            );
                          },
                        })
                        .add({
                          'data-testid': testIds.workshop.interestDetail('overview-vehicle-remove'),
                          type: 'button',
                          title: i18n.t('general.actions.remove'),
                          variant: 'outlined',
                          leftIcon: 'action/delete',
                          isLoading: isRemoving,
                          isDisabled: !hasRemoveVehiclePermission,
                          onClick: () =>
                            openDeleteDialog({
                              'data-testid':
                                testIds.workshop.interestDetail('overview-vehicle-remove'),
                              onConfirm: onRemoveVehicle,
                            }),
                        }),
                    },
                  ]}
                />
              </Card>
            </GridItem>
          )}
        </Show>
        <Show when={!isVehicleSelected && hasRequestSpecificationData(customerInquiry)}>
          <GridItem span={2}>
            <Card title={i18n.t('entity.interest.labels.requestSpecification')}>
              <RequestSpecification
                data-testid={testIds.workshop.interestDetail('overview-requestSpecification')}
                customerInquiry={customerInquiry}
              />
            </Card>
          </GridItem>
        </Show>
        <GridItem span={[2, 1]}>
          <VStack spacing={4}>
            <Show when={hasReadCustomFieldsPermission}>
              <EntityCustomFields
                recordId={props.interestId}
                resourceId={EntityResourceIds.workshopInterest}
                isEditable={hasEditCustomFieldsPermission}
                data-testid={testIds.workshop.interestDetail('workshop-interest-custom-fields')}
              />
            </Show>
            <Show when={hasReadCommentsPermission}>
              <CommentsWidget
                resourceId={EntityResourceIds.workshopInterest}
                recordId={props.interestId}
                isAddDisabled={!hasAddCommentPermission}
                isEditDisabled={!hasUpdateCommentPermission}
                isDeleteDisabled={!hasDeleteCommentPermission}
                data-testid={testIds.workshop.interestDetail('comments')}
              />
            </Show>
          </VStack>
        </GridItem>
        <GridItem span={[2, 1]}>
          <NoteWidget
            isEditable={hasSetNotePermission}
            resourceId={EntityResourceIds.workshopInterest}
            recordId={props.interestId}
            context={EntityResourceIds.workshopInterest}
            data-testid={testIds.workshop.interestDetail('note')}
          />
        </GridItem>
      </Grid>
    </Section>
  );
}
